import React, { useCallback, useEffect, useState } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { VideoPlayer } from '../components/VideoPlayer';
import { winnersApi } from '../lib/api';
import { Trophy, TrendingUp, DollarSign, Target, Video, Image as ImageIcon, X, ChevronLeft, ChevronRight } from 'lucide-react';

interface WinnerRow {
  creative_id: string;
  vertical: string | null;
  title: string | null;
  filename: string | null;
  spend: number | null;
  revenue: number | null;
  profit: number | null;
  roi: number | null;
  conversions: number | null;
  creative_type: string | null;
  state: string | null;
  editor_code: string | null;
  script: string | null;
  why_selected: string | null;
  s3_key: string | null;
  media_type?: 'image' | 'video' | 'other' | null;
  thumbnail_url?: string | null;
  s3_url?: string | null;
}

interface WinnerDetail extends WinnerRow {
  media?: {
    id: string;
    file_type: 'image' | 'video' | 'other';
    original_filename: string;
    s3_url: string;
    download_url: string;
    thumbnail_url?: string;
  } | null;
}

const PAGE_SIZE = 24;
const VERTICALS = ['Home Insurance', 'all'];
const SORTS: { value: 'profit' | 'roi' | 'conversions'; label: string }[] = [
  { value: 'profit', label: 'Profit' },
  { value: 'roi', label: 'ROI' },
  { value: 'conversions', label: 'Conversions' },
];

function money(n: number | null | undefined): string {
  if (n == null || isNaN(Number(n))) return '—';
  return `$${Math.round(Number(n)).toLocaleString('en-US')}`;
}
function pct(n: number | null | undefined): string {
  if (n == null || isNaN(Number(n))) return '—';
  return `${Math.round(Number(n))}%`;
}
function count(n: number | null | undefined): string {
  if (n == null || isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('en-US');
}

export function WinnersLibrary() {
  const [rows, setRows] = useState<WinnerRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [vertical, setVertical] = useState('Home Insurance');
  const [sort, setSort] = useState<'profit' | 'roi' | 'conversions'>('profit');
  const [type, setType] = useState('all');
  const [types, setTypes] = useState<{ type: string; count: number }[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const [selected, setSelected] = useState<WinnerDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await winnersApi.list({ vertical, type, search: search || undefined, page, limit: PAGE_SIZE, sort });
      const data = res.data?.data || {};
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to load winners:', err);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [vertical, type, search, page, sort]);

  useEffect(() => { fetchList(); }, [fetchList]);
  // Reset to page 1 when filter/sort/search changes.
  useEffect(() => { setPage(1); }, [vertical, type, sort, search]);

  // Debounce the search box so we query 350ms after the user stops typing.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Load the available creative types for the current vertical; reset the type
  // filter whenever the vertical changes so it never points at a stale value.
  useEffect(() => {
    setType('all');
    winnersApi.types(vertical)
      .then((res) => setTypes(res.data?.data || []))
      .catch(() => setTypes([]));
  }, [vertical]);

  const openWinner = async (id: string) => {
    setDetailLoading(true);
    setSelected({} as WinnerDetail); // open shell immediately
    try {
      const res = await winnersApi.get(id);
      setSelected(res.data?.data || null);
    } catch (err) {
      console.error('Failed to load winner:', err);
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <DashboardLayout title="Winners Library" badge="admin / ceo only">
      <div className="space-y-6">
        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground">Vertical</label>
            <select
              value={vertical}
              onChange={(e) => setVertical(e.target.value)}
              className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm"
            >
              {VERTICALS.map((v) => (
                <option key={v} value={v}>{v === 'all' ? 'All verticals' : v}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground">Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm"
            >
              <option value="all">All types</option>
              {types.map((t) => (
                <option key={t.type} value={t.type}>{t.type} ({t.count})</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground">Sort by</label>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as any)}
              className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm"
            >
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search filename…"
              className="w-56 rounded-lg border border-border bg-background px-3 py-1.5 text-sm"
            />
          </div>
          <div className="ml-auto text-sm text-muted-foreground tabular-nums">
            {total} winner{total === 1 ? '' : 's'}
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-96">
            <p className="text-muted-foreground">Loading...</p>
          </div>
        ) : rows.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {rows.map((w) => (
              <Card
                key={w.creative_id}
                className="overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary/40 transition-shadow"
                onClick={() => openWinner(w.creative_id)}
              >
                <div className="aspect-video bg-muted relative">
                  {w.thumbnail_url ? (
                    <img src={w.thumbnail_url} alt={w.title || ''} className="w-full h-full object-cover" />
                  ) : w.s3_url && w.media_type === 'video' ? (
                    // No stored thumbnail → render a still frame from the video itself
                    // (metadata-only fetch; #t seeks to ~1s so cards show a real poster).
                    <video
                      src={`${w.s3_url}#t=1`}
                      muted
                      playsInline
                      preload="metadata"
                      className="w-full h-full object-cover pointer-events-none"
                    />
                  ) : w.s3_url && w.media_type === 'image' ? (
                    <img src={w.s3_url} alt={w.title || ''} className="w-full h-full object-cover" />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      {w.media_type === 'image'
                        ? <ImageIcon className="w-14 h-14 text-muted-foreground" />
                        : <Video className="w-14 h-14 text-muted-foreground" />}
                    </div>
                  )}
                  {w.creative_type && (
                    <span className="absolute top-2 left-2 px-2 py-1 text-xs font-semibold rounded bg-background/80 backdrop-blur uppercase">
                      {w.creative_type}
                    </span>
                  )}
                  <span className="absolute top-2 right-2 px-2 py-1 text-xs font-bold rounded bg-emerald-600 text-white">
                    {sort === 'roi' ? pct(w.roi) : sort === 'conversions' ? count(w.conversions) : money(w.profit)}
                  </span>
                </div>
                <div className="p-3 space-y-2">
                  <h3 className="font-medium text-sm truncate" title={w.title || ''}>{w.title || w.filename}</h3>
                  <div className="flex flex-wrap gap-1">
                    {w.state && <Chip>{w.state}</Chip>}
                    {w.script && <Chip>{w.script}</Chip>}
                    {w.editor_code && <Chip>{w.editor_code}</Chip>}
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
                    <span><DollarSign className="inline w-3 h-3 mr-0.5" />{money(w.profit)}</span>
                    <span><TrendingUp className="inline w-3 h-3 mr-0.5" />{pct(w.roi)}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                <Trophy className="w-8 h-8 text-muted-foreground" />
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">No winners yet</h3>
                <p className="text-muted-foreground">Winning creatives for this vertical will appear here.</p>
              </div>
            </div>
          </Card>
        )}

        {/* Pagination */}
        {!loading && total > 0 && (
          <div className="flex items-center justify-center gap-4 pt-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Prev
            </Button>
            <span className="tabular-nums text-sm text-muted-foreground">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>

      {/* Split detail modal */}
      {selected !== null && (
        <WinnerModal
          winner={selected}
          loading={detailLoading}
          onClose={() => setSelected(null)}
        />
      )}
    </DashboardLayout>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="px-2 py-0.5 text-xs rounded-full bg-accent/20 text-accent-foreground font-medium">
      {children}
    </span>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function WinnerModal({ winner, loading, onClose }: { winner: WinnerDetail; loading: boolean; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const media = winner.media;
  const isVideo = media?.file_type === 'video';
  const isImage = media?.file_type === 'image';
  const mediaUrl = media?.s3_url;

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-card rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col md:flex-row"
        onClick={(e) => e.stopPropagation()}
      >
        {/* LEFT: media */}
        <div className="md:w-1/2 bg-black flex items-center justify-center min-h-[240px] md:min-h-[480px] p-2">
          {loading ? (
            <p className="text-white/70 text-sm">Loading media...</p>
          ) : isVideo && mediaUrl ? (
            <VideoPlayer src={mediaUrl} poster={media?.thumbnail_url} />
          ) : isImage && mediaUrl ? (
            <img src={mediaUrl} alt={winner.title || ''} className="max-w-full max-h-[80vh] object-contain" />
          ) : (
            <p className="text-white/60 text-sm px-6 text-center">Preview unavailable</p>
          )}
        </div>

        {/* RIGHT: metrics + why it won */}
        <div className="md:w-1/2 flex flex-col overflow-y-auto">
          <div className="flex items-start justify-between p-4 border-b border-border">
            <div className="min-w-0 pr-2">
              <h2 className="font-semibold truncate" title={winner.title || ''}>{winner.title || winner.filename}</h2>
              <p className="text-xs text-muted-foreground">{winner.vertical}</p>
            </div>
            <button onClick={onClose} className="p-1 rounded hover:bg-accent/40" title="Close (Esc)">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 space-y-4">
            <div className="flex flex-wrap gap-1.5">
              {winner.creative_type && <Chip>{winner.creative_type}</Chip>}
              {winner.state && <Chip>{winner.state}</Chip>}
              {winner.script && <Chip>{winner.script}</Chip>}
              {winner.editor_code && <Chip>Editor {winner.editor_code}</Chip>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Metric label="Spend" value={money(winner.spend)} />
              <Metric label="Revenue" value={money(winner.revenue)} />
              <Metric label="Profit" value={money(winner.profit)} />
              <Metric label="ROI" value={pct(winner.roi)} />
              <Metric label="Conversions" value={count(winner.conversions)} />
            </div>

            {winner.why_selected && (
              <div>
                <h3 className="text-sm font-semibold flex items-center gap-1.5 mb-1">
                  <Target className="w-4 h-4" /> Why it won
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{winner.why_selected}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default WinnersLibrary;
